import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Eye, Save, X, Plus, Trash2 } from 'lucide-react';
import type { EmailTemplate, EmailTemplateType, UpdateEmailTemplateDto } from '@yawi/types';
import { Button, Input, Select, Spinner, Textarea } from '@/core/ui/components';
import { useEmailTemplates } from '../hooks/useEmailTemplates';

const TYPE_OPTIONS = [
  { value: 'welcome', label: 'Bienvenida' },
  { value: 'password_reset', label: 'Restablecer Contraseña' },
  { value: 'invitation', label: 'Invitación' },
  { value: 'payment_reminder', label: 'Recordatorio de Pago' },
  { value: 'payment_confirmation', label: 'Confirmación de Pago' },
  { value: 'reservation_confirmation', label: 'Confirmación de Reserva' },
  { value: 'announcement', label: 'Anuncio' },
  { value: 'custom', label: 'Personalizado' },
];

export function EmailTemplateDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isNew = id === 'new';
  const { getTemplateById, createTemplate, updateTemplate, previewTemplate, isSaving } =
    useEmailTemplates();

  const [isLoading, setIsLoading] = useState(!isNew);
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string>('');
  const [previewSubject, setPreviewSubject] = useState<string>('');

  const [formData, setFormData] = useState<{
    name: string;
    type: EmailTemplateType;
    subject: string;
    bodyHtml: string;
    bodyText: string;
    variables: string[];
    isActive: boolean;
  }>({
    name: '',
    type: 'custom',
    subject: '',
    bodyHtml: '',
    bodyText: '',
    variables: [],
    isActive: true,
  });

  const [newVariable, setNewVariable] = useState('');

  useEffect(() => {
    if (!isNew && id) {
      loadTemplate(id);
    }
  }, [id, isNew]);

  const loadTemplate = async (templateId: string) => {
    try {
      const template = await getTemplateById(templateId);
      setFormData({
        name: template.name,
        type: template.type,
        subject: template.subject,
        bodyHtml: template.bodyHtml,
        bodyText: template.bodyText || '',
        variables: template.variables || [],
        isActive: template.isActive,
      });
    } catch (err) {
      console.error('Failed to load template:', err);
      navigate('/email-templates');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (isNew) {
        const template = await createTemplate({
          type: formData.type,
          name: formData.name,
          subject: formData.subject,
          bodyHtml: formData.bodyHtml,
          bodyText: formData.bodyText || undefined,
          variables: formData.variables,
          isActive: formData.isActive,
        });
        navigate(`/email-templates/${template.id}`);
      } else if (id) {
        await updateTemplate(id, {
          name: formData.name,
          subject: formData.subject,
          bodyHtml: formData.bodyHtml,
          bodyText: formData.bodyText || undefined,
          variables: formData.variables,
          isActive: formData.isActive,
        });
      }
    } catch {
      // Error already handled in hook
    }
  };

  const handlePreview = async () => {
    try {
      const sampleVariables: Record<string, string> = {};
      formData.variables.forEach((v) => {
        sampleVariables[v] = `[${v}]`;
      });

      const preview = await previewTemplate({
        subject: formData.subject,
        bodyHtml: formData.bodyHtml,
        variables: sampleVariables,
      });
      setPreviewSubject(preview.subject);
      setPreviewHtml(preview.bodyHtml);
      setShowPreview(true);
    } catch {
      // Fallback to local preview
      setPreviewSubject(formData.subject);
      setPreviewHtml(formData.bodyHtml);
      setShowPreview(true);
    }
  };

  const addVariable = () => {
    if (newVariable.trim() && !formData.variables.includes(newVariable.trim())) {
      setFormData((prev) => ({
        ...prev,
        variables: [...prev.variables, newVariable.trim()],
      }));
      setNewVariable('');
    }
  };

  const removeVariable = (variable: string) => {
    setFormData((prev) => ({
      ...prev,
      variables: prev.variables.filter((v) => v !== variable),
    }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="h-full">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-800 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/email-templates')}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </button>
            <div>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                {isNew ? 'Nueva Plantilla' : 'Editar Plantilla'}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {isNew
                  ? 'Crear una nueva plantilla de correo'
                  : 'Modifica los campos de la plantilla'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={handlePreview} leftIcon={<Eye className="w-4 h-4" />}>
              Vista Previa
            </Button>
            <Button onClick={handleSubmit} isLoading={isSaving} leftIcon={<Save className="w-4 h-4" />}>
              Guardar
            </Button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="Nombre"
              value={formData.name}
              onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="Nombre de la plantilla"
              required
            />
            <Select
              label="Tipo"
              value={formData.type}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, type: e.target.value as EmailTemplateType }))
              }
              options={TYPE_OPTIONS}
              disabled={!isNew}
            />
          </div>

          <Input
            label="Asunto"
            value={formData.subject}
            onChange={(e) => setFormData((prev) => ({ ...prev, subject: e.target.value }))}
            placeholder="Asunto del correo (usa {{variable}} para variables)"
            required
          />

          <Textarea
            label="Contenido HTML"
            value={formData.bodyHtml}
            onChange={(e) => setFormData((prev) => ({ ...prev, bodyHtml: e.target.value }))}
            placeholder="<html>...</html>"
            rows={12}
            required
          />

          <Textarea
            label="Contenido de Texto (opcional)"
            value={formData.bodyText}
            onChange={(e) => setFormData((prev) => ({ ...prev, bodyText: e.target.value }))}
            placeholder="Versión de texto plano del correo"
            rows={4}
          />

          {/* Variables */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Variables
            </label>
            <div className="flex flex-wrap gap-2 mb-3">
              {formData.variables.map((variable) => (
                <span
                  key={variable}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full text-sm"
                >
                  {`{{${variable}}}`}
                  <button
                    type="button"
                    onClick={() => removeVariable(variable)}
                    className="hover:text-purple-900 dark:hover:text-purple-100"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={newVariable}
                onChange={(e) => setNewVariable(e.target.value)}
                placeholder="nombre_variable"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addVariable();
                  }
                }}
              />
              <Button type="button" variant="outline" onClick={addVariable}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Las variables se usan como {'{{nombre_variable}}'} en el contenido
            </p>
          </div>

          {/* Active toggle */}
          <div className="flex items-center gap-3">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData((prev) => ({ ...prev, isActive: e.target.checked }))}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600" />
            </label>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Plantilla activa
            </span>
          </div>
        </form>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Vista Previa
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Asunto: {previewSubject}
                </p>
              </div>
              <button
                onClick={() => setShowPreview(false)}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>
            <div className="p-4 overflow-auto max-h-[calc(90vh-100px)]">
              <div
                className="bg-white border border-gray-200 rounded-lg p-4"
                dangerouslySetInnerHTML={{ __html: previewHtml }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
